/**
 * Initials-based avatar fallback. Deterministically picks a background
 * color from the user's name so the same user always gets the same color.
 */

const PALETTE = [
  'bg-blue-600',
  'bg-emerald-600',
  'bg-violet-600',
  'bg-amber-600',
  'bg-rose-600',
  'bg-cyan-600',
  'bg-indigo-600',
  'bg-teal-600',
  'bg-fuchsia-600',
  'bg-orange-600',
];

function hashString(s: string): number {
  let hash = 0;
  for (let i = 0; i < s.length; i++) {
    hash = ((hash << 5) - hash + s.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

function getInitials(name?: string, email?: string): string {
  if (name && name.trim()) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return parts[0][0].toUpperCase();
  }
  if (email) {
    return email[0].toUpperCase();
  }
  return '?';
}

interface DefaultAvatarProps {
  name?: string;
  email?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const SIZES = {
  sm: 'w-8 h-8 text-xs',
  md: 'w-10 h-10 text-sm',
  lg: 'w-20 h-20 text-2xl',
};

export default function DefaultAvatar({ name, email, size = 'md', className = '' }: DefaultAvatarProps) {
  const initials = getInitials(name, email);
  const seed = name || email || '';
  const bg = PALETTE[hashString(seed) % PALETTE.length];

  return (
    <div
      className={`${SIZES[size]} ${bg} rounded-full flex items-center justify-center font-semibold text-white select-none ${className}`}
      title={name || email}
    >
      {initials}
    </div>
  );
}
